// Japan postcode -> { city, prefecture } lookup.
//
// Ported verbatim from the reference implementation
// C:\Projects\dxpanel\JP\get_city_by_postcode.php ($postcodeMap).
//
// Keyed by the FIRST 3 DIGITS of a 7-digit Japanese postcode (NNN-NNNN).
// The reference PHP looks up substr(postcodeClean, 0, 3) only.
//
// PHP last-wins semantics: the source PHP array literal contains duplicate
// keys (010-017 appear under both Hokkaido and Akita; 630-639 under both
// Hyogo and Nara). PHP keeps the LAST assignment for a duplicate key.
// The entries below are written in the SAME file order as the PHP source,
// so the later assignment overwrites the earlier one when this object is
// constructed — producing identical results to PHP (010-017 -> Akita,
// 630-639 -> Nara).
//
// Used only by the Japan basic-profile layout. Do not edit by hand to
// "fix" geographically odd entries — fidelity to the reference is the
// contract here.

export interface JpPostcodeEntry {
    city: string;
    prefecture: string;
}

export const JP_POSTCODE_MAP: Record<string, JpPostcodeEntry> = {
    // Hokkaido (001-099)
    '001': { city: 'Sapporo', prefecture: 'Hokkaido' },
    '002': { city: 'Sapporo', prefecture: 'Hokkaido' },
    '003': { city: 'Sapporo', prefecture: 'Hokkaido' },
    '004': { city: 'Sapporo', prefecture: 'Hokkaido' },
    '005': { city: 'Sapporo', prefecture: 'Hokkaido' },
    '006': { city: 'Sapporo', prefecture: 'Hokkaido' },
    '007': { city: 'Sapporo', prefecture: 'Hokkaido' },
    '008': { city: 'Asahikawa', prefecture: 'Hokkaido' },
    '009': { city: 'Asahikawa', prefecture: 'Hokkaido' },
    '010': { city: 'Obihiro', prefecture: 'Hokkaido' },
    '011': { city: 'Obihiro', prefecture: 'Hokkaido' },
    '012': { city: 'Kushiro', prefecture: 'Hokkaido' },
    '013': { city: 'Kushiro', prefecture: 'Hokkaido' },
    '014': { city: 'Nemuro', prefecture: 'Hokkaido' },
    '015': { city: 'Hakodate', prefecture: 'Hokkaido' },
    '016': { city: 'Hakodate', prefecture: 'Hokkaido' },
    '017': { city: 'Aomori', prefecture: 'Hokkaido' },
    '018': { city: 'Muroran', prefecture: 'Hokkaido' },
    '019': { city: 'Tomakomai', prefecture: 'Hokkaido' },

    // Aomori (030-039)
    '030': { city: 'Aomori', prefecture: 'Aomori' },
    '031': { city: 'Aomori', prefecture: 'Aomori' },
    '032': { city: 'Hirosaki', prefecture: 'Aomori' },
    '033': { city: 'Hirosaki', prefecture: 'Aomori' },
    '034': { city: 'Hachinohe', prefecture: 'Aomori' },
    '035': { city: 'Hachinohe', prefecture: 'Aomori' },
    '036': { city: 'Goshogawara', prefecture: 'Aomori' },
    '037': { city: 'Tsugaru', prefecture: 'Aomori' },
    '038': { city: 'Mutsu', prefecture: 'Aomori' },
    '039': { city: 'Towada', prefecture: 'Aomori' },

    // Iwate (020-029)
    '020': { city: 'Morioka', prefecture: 'Iwate' },
    '021': { city: 'Morioka', prefecture: 'Iwate' },
    '022': { city: 'Kuji', prefecture: 'Iwate' },
    '023': { city: 'Miyako', prefecture: 'Iwate' },
    '024': { city: 'Kamaishi', prefecture: 'Iwate' },
    '025': { city: 'Ofunato', prefecture: 'Iwate' },
    '026': { city: 'Rikuzentakata', prefecture: 'Iwate' },
    '027': { city: 'Ichinoseki', prefecture: 'Iwate' },
    '028': { city: 'Oshu', prefecture: 'Iwate' },
    '029': { city: 'Kamaishi', prefecture: 'Iwate' },

    // Miyagi (980-989)
    '980': { city: 'Sendai', prefecture: 'Miyagi' },
    '981': { city: 'Sendai', prefecture: 'Miyagi' },
    '982': { city: 'Sendai', prefecture: 'Miyagi' },
    '983': { city: 'Sendai', prefecture: 'Miyagi' },
    '984': { city: 'Sendai', prefecture: 'Miyagi' },
    '985': { city: 'Ishinomaki', prefecture: 'Miyagi' },
    '986': { city: 'Kesennuma', prefecture: 'Miyagi' },
    '987': { city: 'Shiogama', prefecture: 'Miyagi' },
    '988': { city: 'Shichigahama', prefecture: 'Miyagi' },
    '989': { city: 'Natori', prefecture: 'Miyagi' },

    // Akita (010-019) — duplicate keys 010-017 overwrite Hokkaido above (PHP last-wins)
    '010': { city: 'Akita', prefecture: 'Akita' },
    '011': { city: 'Akita', prefecture: 'Akita' },
    '012': { city: 'Yokote', prefecture: 'Akita' },
    '013': { city: 'Yokote', prefecture: 'Akita' },
    '014': { city: 'Oga', prefecture: 'Akita' },
    '015': { city: 'Katagami', prefecture: 'Akita' },
    '016': { city: 'Odate', prefecture: 'Akita' },
    '017': { city: 'Kazuno', prefecture: 'Akita' },

    // Yamagata (990-999)
    '990': { city: 'Yamagata', prefecture: 'Yamagata' },
    '991': { city: 'Yamagata', prefecture: 'Yamagata' },
    '992': { city: 'Yonezawa', prefecture: 'Yamagata' },
    '993': { city: 'Murayama', prefecture: 'Yamagata' },
    '994': { city: 'Sagae', prefecture: 'Yamagata' },
    '995': { city: 'Kaminoyama', prefecture: 'Yamagata' },
    '996': { city: 'Tsuruoka', prefecture: 'Yamagata' },
    '997': { city: 'Shonai', prefecture: 'Yamagata' },
    '998': { city: 'Atsumi', prefecture: 'Yamagata' },
    '999': { city: 'Oishida', prefecture: 'Yamagata' },

    // Fukushima (960-969)
    '960': { city: 'Fukushima', prefecture: 'Fukushima' },
    '961': { city: 'Fukushima', prefecture: 'Fukushima' },
    '962': { city: 'Koriyama', prefecture: 'Fukushima' },
    '963': { city: 'Koriyama', prefecture: 'Fukushima' },
    '964': { city: 'Ishikawa', prefecture: 'Fukushima' },
    '965': { city: 'Aizuwakamatsu', prefecture: 'Fukushima' },
    '966': { city: 'Aizuwakamatsu', prefecture: 'Fukushima' },
    '967': { city: 'Kitakata', prefecture: 'Fukushima' },
    '968': { city: 'Yanaizu', prefecture: 'Fukushima' },
    '969': { city: 'Motomiya', prefecture: 'Fukushima' },

    // Ibaraki (310-319)
    '310': { city: 'Mito', prefecture: 'Ibaraki' },
    '311': { city: 'Hitachi', prefecture: 'Ibaraki' },
    '312': { city: 'Ōtsu', prefecture: 'Ibaraki' },
    '313': { city: 'Katsuta', prefecture: 'Ibaraki' },
    '314': { city: 'Kasama', prefecture: 'Ibaraki' },
    '315': { city: 'Jōyo', prefecture: 'Ibaraki' },
    '316': { city: 'Chikusei', prefecture: 'Ibaraki' },
    '317': { city: 'Koga', prefecture: 'Ibaraki' },
    '318': { city: 'Tsukuba', prefecture: 'Ibaraki' },
    '319': { city: 'Tsuchiura', prefecture: 'Ibaraki' },

    // Tochigi (320-329)
    '320': { city: 'Utsunomiya', prefecture: 'Tochigi' },
    '321': { city: 'Kanuma', prefecture: 'Tochigi' },
    '322': { city: 'Kanuma', prefecture: 'Tochigi' },
    '323': { city: 'Nikko', prefecture: 'Tochigi' },
    '324': { city: 'Akagi', prefecture: 'Tochigi' },
    '325': { city: 'Nasu', prefecture: 'Tochigi' },
    '326': { city: 'Nasushiobara', prefecture: 'Tochigi' },
    '327': { city: 'Mooka', prefecture: 'Tochigi' },
    '328': { city: 'Otawara', prefecture: 'Tochigi' },
    '329': { city: 'Takanezawa', prefecture: 'Tochigi' },

    // Gunma (370-379)
    '370': { city: 'Maebashi', prefecture: 'Gunma' },
    '371': { city: 'Takasaki', prefecture: 'Gunma' },
    '372': { city: 'Tatebayashi', prefecture: 'Gunma' },
    '373': { city: 'Isesaki', prefecture: 'Gunma' },
    '374': { city: 'Ōta', prefecture: 'Gunma' },
    '375': { city: 'Kiryu', prefecture: 'Gunma' },
    '376': { city: 'Numata', prefecture: 'Gunma' },
    '377': { city: 'Midori', prefecture: 'Gunma' },
    '378': { city: 'Shibukawa', prefecture: 'Gunma' },
    '379': { city: 'Tomioka', prefecture: 'Gunma' },

    // Saitama (330-369)
    '330': { city: 'Saitama', prefecture: 'Saitama' },
    '331': { city: 'Kawagoe', prefecture: 'Saitama' },
    '332': { city: 'Kawagoe', prefecture: 'Saitama' },
    '333': { city: 'Kawagoe', prefecture: 'Saitama' },
    '334': { city: 'Moroyama', prefecture: 'Saitama' },
    '335': { city: 'Moroyama', prefecture: 'Saitama' },
    '336': { city: 'Tsurugashima', prefecture: 'Saitama' },
    '337': { city: 'Hidaka', prefecture: 'Saitama' },
    '338': { city: 'Tokorozawa', prefecture: 'Saitama' },
    '339': { city: 'Sakado', prefecture: 'Saitama' },
    '340': { city: 'Kasukabe', prefecture: 'Saitama' },
    '341': { city: 'Yoshii', prefecture: 'Saitama' },
    '342': { city: 'Kuki', prefecture: 'Saitama' },
    '343': { city: 'Koshigaya', prefecture: 'Saitama' },
    '344': { city: 'Yashio', prefecture: 'Saitama' },
    '345': { city: 'Warabi', prefecture: 'Saitama' },
    '346': { city: 'Soka', prefecture: 'Saitama' },
    '347': { city: 'Shiraoka', prefecture: 'Saitama' },
    '348': { city: 'Hasuda', prefecture: 'Saitama' },
    '349': { city: 'Hasuda', prefecture: 'Saitama' },
    '350': { city: 'Kawagoe', prefecture: 'Saitama' },
    '351': { city: 'Iruma', prefecture: 'Saitama' },
    '352': { city: 'Fujimi', prefecture: 'Saitama' },
    '353': { city: 'Yoshimi', prefecture: 'Saitama' },
    '354': { city: 'Ogano', prefecture: 'Saitama' },
    '355': { city: 'Chichibu', prefecture: 'Saitama' },
    '356': { city: 'Hanno', prefecture: 'Saitama' },
    '357': { city: 'Higashimatsuyama', prefecture: 'Saitama' },
    '358': { city: 'Kumagaya', prefecture: 'Saitama' },
    '359': { city: 'Jyoetsu', prefecture: 'Saitama' },
    '360': { city: 'Kumagaya', prefecture: 'Saitama' },
    '361': { city: 'Gyoda', prefecture: 'Saitama' },
    '362': { city: 'Ōmiya', prefecture: 'Saitama' },
    '363': { city: 'Ōmiya', prefecture: 'Saitama' },
    '364': { city: 'Kawaguchi', prefecture: 'Saitama' },
    '365': { city: 'Urawa', prefecture: 'Saitama' },
    '366': { city: 'Urawa', prefecture: 'Saitama' },
    '367': { city: 'Urawa', prefecture: 'Saitama' },
    '368': { city: 'Iwatsuki', prefecture: 'Saitama' },
    '369': { city: 'Misato', prefecture: 'Saitama' },

    // Tokyo (100-209)
    '100': { city: 'Chiyoda', prefecture: 'Tokyo' },
    '101': { city: 'Chiyoda', prefecture: 'Tokyo' },
    '102': { city: 'Chiyoda', prefecture: 'Tokyo' },
    '103': { city: 'Chuo', prefecture: 'Tokyo' },
    '104': { city: 'Chuo', prefecture: 'Tokyo' },
    '105': { city: 'Minato', prefecture: 'Tokyo' },
    '106': { city: 'Minato', prefecture: 'Tokyo' },
    '107': { city: 'Minato', prefecture: 'Tokyo' },
    '108': { city: 'Minato', prefecture: 'Tokyo' },
    '109': { city: 'Minato', prefecture: 'Tokyo' },
    '110': { city: 'Taito', prefecture: 'Tokyo' },
    '111': { city: 'Taito', prefecture: 'Tokyo' },
    '112': { city: 'Taito', prefecture: 'Tokyo' },
    '113': { city: 'Arakawa', prefecture: 'Tokyo' },
    '114': { city: 'Adachi', prefecture: 'Tokyo' },
    '115': { city: 'Adachi', prefecture: 'Tokyo' },
    '116': { city: 'Adachi', prefecture: 'Tokyo' },
    '117': { city: 'Adachi', prefecture: 'Tokyo' },
    '118': { city: 'Sumida', prefecture: 'Tokyo' },
    '119': { city: 'Sumida', prefecture: 'Tokyo' },
    '120': { city: 'Edogawa', prefecture: 'Tokyo' },
    '121': { city: 'Edogawa', prefecture: 'Tokyo' },
    '122': { city: 'Edogawa', prefecture: 'Tokyo' },
    '123': { city: 'Edogawa', prefecture: 'Tokyo' },
    '124': { city: 'Edogawa', prefecture: 'Tokyo' },
    '125': { city: 'Edogawa', prefecture: 'Tokyo' },
    '126': { city: 'Edogawa', prefecture: 'Tokyo' },
    '127': { city: 'Edogawa', prefecture: 'Tokyo' },
    '128': { city: 'Edogawa', prefecture: 'Tokyo' },
    '129': { city: 'Katsushika', prefecture: 'Tokyo' },
    '130': { city: 'Sumida', prefecture: 'Tokyo' },
    '131': { city: 'Sumida', prefecture: 'Tokyo' },
    '132': { city: 'Sumida', prefecture: 'Tokyo' },
    '133': { city: 'Sumida', prefecture: 'Tokyo' },
    '134': { city: 'Sumida', prefecture: 'Tokyo' },
    '135': { city: 'Sumida', prefecture: 'Tokyo' },
    '136': { city: 'Sumida', prefecture: 'Tokyo' },
    '137': { city: 'Sumida', prefecture: 'Tokyo' },
    '138': { city: 'Sumida', prefecture: 'Tokyo' },
    '139': { city: 'Sumida', prefecture: 'Tokyo' },
    '140': { city: 'Shinagawa', prefecture: 'Tokyo' },
    '141': { city: 'Shinagawa', prefecture: 'Tokyo' },
    '142': { city: 'Shinagawa', prefecture: 'Tokyo' },
    '143': { city: 'Shinagawa', prefecture: 'Tokyo' },
    '144': { city: 'Shinagawa', prefecture: 'Tokyo' },
    '145': { city: 'Shinagawa', prefecture: 'Tokyo' },
    '146': { city: 'Shinagawa', prefecture: 'Tokyo' },
    '147': { city: 'Shinagawa', prefecture: 'Tokyo' },
    '148': { city: 'Shinagawa', prefecture: 'Tokyo' },
    '149': { city: 'Shinagawa', prefecture: 'Tokyo' },
    '150': { city: 'Shibuya', prefecture: 'Tokyo' },
    '151': { city: 'Shibuya', prefecture: 'Tokyo' },
    '152': { city: 'Minato', prefecture: 'Tokyo' },
    '153': { city: 'Minato', prefecture: 'Tokyo' },
    '154': { city: 'Minato', prefecture: 'Tokyo' },
    '155': { city: 'Minato', prefecture: 'Tokyo' },
    '156': { city: 'Setagaya', prefecture: 'Tokyo' },
    '157': { city: 'Setagaya', prefecture: 'Tokyo' },
    '158': { city: 'Setagaya', prefecture: 'Tokyo' },
    '159': { city: 'Setagaya', prefecture: 'Tokyo' },
    '160': { city: 'Shinjuku', prefecture: 'Tokyo' },
    '161': { city: 'Shinjuku', prefecture: 'Tokyo' },
    '162': { city: 'Shinjuku', prefecture: 'Tokyo' },
    '163': { city: 'Shinjuku', prefecture: 'Tokyo' },
    '164': { city: 'Nakano', prefecture: 'Tokyo' },
    '165': { city: 'Nakano', prefecture: 'Tokyo' },
    '166': { city: 'Suginami', prefecture: 'Tokyo' },
    '167': { city: 'Suginami', prefecture: 'Tokyo' },
    '168': { city: 'Suginami', prefecture: 'Tokyo' },
    '169': { city: 'Suginami', prefecture: 'Tokyo' },
    '170': { city: 'Toshima', prefecture: 'Tokyo' },
    '171': { city: 'Toshima', prefecture: 'Tokyo' },
    '172': { city: 'Toshima', prefecture: 'Tokyo' },
    '173': { city: 'Toshima', prefecture: 'Tokyo' },
    '174': { city: 'Itabashi', prefecture: 'Tokyo' },
    '175': { city: 'Itabashi', prefecture: 'Tokyo' },
    '176': { city: 'Itabashi', prefecture: 'Tokyo' },
    '177': { city: 'Itabashi', prefecture: 'Tokyo' },
    '178': { city: 'Itabashi', prefecture: 'Tokyo' },
    '179': { city: 'Itabashi', prefecture: 'Tokyo' },
    '180': { city: 'Kita', prefecture: 'Tokyo' },
    '181': { city: 'Kita', prefecture: 'Tokyo' },
    '182': { city: 'Kita', prefecture: 'Tokyo' },
    '183': { city: 'Kita', prefecture: 'Tokyo' },
    '184': { city: 'Kita', prefecture: 'Tokyo' },
    '185': { city: 'Kita', prefecture: 'Tokyo' },
    '186': { city: 'Kita', prefecture: 'Tokyo' },
    '187': { city: 'Kita', prefecture: 'Tokyo' },
    '188': { city: 'Kita', prefecture: 'Tokyo' },
    '189': { city: 'Kiyose', prefecture: 'Tokyo' },
    '190': { city: 'Fuchu', prefecture: 'Tokyo' },
    '191': { city: 'Chofu', prefecture: 'Tokyo' },
    '192': { city: 'Chofu', prefecture: 'Tokyo' },
    '193': { city: 'Komae', prefecture: 'Tokyo' },
    '194': { city: 'Setagaya', prefecture: 'Tokyo' },
    '195': { city: 'Setagaya', prefecture: 'Tokyo' },
    '196': { city: 'Setagaya', prefecture: 'Tokyo' },
    '197': { city: 'Setagaya', prefecture: 'Tokyo' },
    '198': { city: 'Setagaya', prefecture: 'Tokyo' },
    '199': { city: 'Setagaya', prefecture: 'Tokyo' },
    '200': { city: 'Higashiyamato', prefecture: 'Tokyo' },
    '201': { city: 'Higashiyamato', prefecture: 'Tokyo' },
    '202': { city: 'Higashiyamato', prefecture: 'Tokyo' },
    '203': { city: 'Kodaira', prefecture: 'Tokyo' },
    '204': { city: 'Murayama', prefecture: 'Tokyo' },
    '205': { city: 'Fussa', prefecture: 'Tokyo' },
    '206': { city: 'Akishima', prefecture: 'Tokyo' },
    '207': { city: 'Hamura', prefecture: 'Tokyo' },
    '208': { city: 'Mizuho', prefecture: 'Tokyo' },
    '209': { city: 'Okutama', prefecture: 'Tokyo' },

    // Kanagawa (210-259)
    '210': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '211': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '212': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '213': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '214': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '215': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '216': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '217': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '218': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '219': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '220': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '221': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '222': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '223': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '224': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '225': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '226': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '227': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '228': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '229': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '230': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '231': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '232': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '233': { city: 'Yokohama', prefecture: 'Kanagawa' },
    '234': { city: 'Kawasaki', prefecture: 'Kanagawa' },
    '235': { city: 'Kawasaki', prefecture: 'Kanagawa' },
    '236': { city: 'Kawasaki', prefecture: 'Kanagawa' },
    '237': { city: 'Kawasaki', prefecture: 'Kanagawa' },
    '238': { city: 'Kawasaki', prefecture: 'Kanagawa' },
    '239': { city: 'Kawasaki', prefecture: 'Kanagawa' },
    '240': { city: 'Kawasaki', prefecture: 'Kanagawa' },
    '241': { city: 'Kawasaki', prefecture: 'Kanagawa' },
    '242': { city: 'Sagamihara', prefecture: 'Kanagawa' },
    '243': { city: 'Sagamihara', prefecture: 'Kanagawa' },
    '244': { city: 'Sagamihara', prefecture: 'Kanagawa' },
    '245': { city: 'Sagamihara', prefecture: 'Kanagawa' },
    '246': { city: 'Yamato', prefecture: 'Kanagawa' },
    '247': { city: 'Asahi', prefecture: 'Kanagawa' },
    '248': { city: 'Zama', prefecture: 'Kanagawa' },
    '249': { city: 'Isehara', prefecture: 'Kanagawa' },
    '250': { city: 'Odawara', prefecture: 'Kanagawa' },
    '251': { city: 'Odawara', prefecture: 'Kanagawa' },
    '252': { city: 'Isehara', prefecture: 'Kanagawa' },
    '253': { city: 'Hadano', prefecture: 'Kanagawa' },
    '254': { city: 'Hadano', prefecture: 'Kanagawa' },
    '255': { city: 'Atsugi', prefecture: 'Kanagawa' },
    '256': { city: 'Atsugi', prefecture: 'Kanagawa' },
    '257': { city: 'Yamakita', prefecture: 'Kanagawa' },
    '258': { city: 'Hakone', prefecture: 'Kanagawa' },
    '259': { city: 'Yugawara', prefecture: 'Kanagawa' },

    // Niigata (950-959)
    '950': { city: 'Niigata', prefecture: 'Niigata' },
    '951': { city: 'Niigata', prefecture: 'Niigata' },
    '952': { city: 'Niigata', prefecture: 'Niigata' },
    '953': { city: 'Niigata', prefecture: 'Niigata' },
    '954': { city: 'Niigata', prefecture: 'Niigata' },
    '955': { city: 'Kashiwazaki', prefecture: 'Niigata' },
    '956': { city: 'Joetsu', prefecture: 'Niigata' },
    '957': { city: 'Itoigawa', prefecture: 'Niigata' },
    '958': { city: 'Myoko', prefecture: 'Niigata' },
    '959': { city: 'Tokamachi', prefecture: 'Niigata' },

    // Toyama (930-939)
    '930': { city: 'Toyama', prefecture: 'Toyama' },
    '931': { city: 'Toyama', prefecture: 'Toyama' },
    '932': { city: 'Takaoka', prefecture: 'Toyama' },
    '933': { city: 'Imizu', prefecture: 'Toyama' },
    '934': { city: 'Oyabe', prefecture: 'Toyama' },
    '935': { city: 'Nanao', prefecture: 'Toyama' },
    '936': { city: 'Himi', prefecture: 'Toyama' },
    '937': { city: 'Kurobe', prefecture: 'Toyama' },
    '938': { city: 'Nanto', prefecture: 'Toyama' },
    '939': { city: 'Tateyama', prefecture: 'Toyama' },

    // Ishikawa (920-929)
    '920': { city: 'Kanazawa', prefecture: 'Ishikawa' },
    '921': { city: 'Kanazawa', prefecture: 'Ishikawa' },
    '922': { city: 'Komatsu', prefecture: 'Ishikawa' },
    '923': { city: 'Kaga', prefecture: 'Ishikawa' },
    '924': { city: 'Kawakita', prefecture: 'Ishikawa' },
    '925': { city: 'Wajima', prefecture: 'Ishikawa' },
    '926': { city: 'Suzu', prefecture: 'Ishikawa' },
    '927': { city: 'Hakui', prefecture: 'Ishikawa' },
    '928': { city: 'Noto', prefecture: 'Ishikawa' },
    '929': { city: 'Kahoku', prefecture: 'Ishikawa' },

    // Fukui (910-919)
    '910': { city: 'Fukui', prefecture: 'Fukui' },
    '911': { city: 'Fukui', prefecture: 'Fukui' },
    '912': { city: 'Awara', prefecture: 'Fukui' },
    '913': { city: 'Sabae', prefecture: 'Fukui' },
    '914': { city: 'Echizen', prefecture: 'Fukui' },
    '915': { city: 'Takefu', prefecture: 'Fukui' },
    '916': { city: 'Ōno', prefecture: 'Fukui' },
    '917': { city: 'Shinminato', prefecture: 'Fukui' },
    '918': { city: 'Tsuruga', prefecture: 'Fukui' },
    '919': { city: 'Minamiechizen', prefecture: 'Fukui' },

    // Yamanashi (400-409)
    '400': { city: 'Kofu', prefecture: 'Yamanashi' },
    '401': { city: 'Kofu', prefecture: 'Yamanashi' },
    '402': { city: 'Yanakaba', prefecture: 'Yamanashi' },
    '403': { city: 'Kofu', prefecture: 'Yamanashi' },
    '404': { city: 'Tsuru', prefecture: 'Yamanashi' },
    '405': { city: 'Otsuki', prefecture: 'Yamanashi' },
    '406': { city: 'Koshu', prefecture: 'Yamanashi' },
    '407': { city: 'Minobu', prefecture: 'Yamanashi' },
    '408': { city: 'Hayakawa', prefecture: 'Yamanashi' },
    '409': { city: 'Ichikawamisato', prefecture: 'Yamanashi' },

    // Nagano (380-389)
    '380': { city: 'Nagano', prefecture: 'Nagano' },
    '381': { city: 'Nagano', prefecture: 'Nagano' },
    '382': { city: 'Yonago', prefecture: 'Nagano' },
    '383': { city: 'Komoro', prefecture: 'Nagano' },
    '384': { city: 'Shiojiri', prefecture: 'Nagano' },
    '385': { city: 'Shiojiri', prefecture: 'Nagano' },
    '386': { city: 'Azumino', prefecture: 'Nagano' },
    '387': { city: 'Iida', prefecture: 'Nagano' },
    '388': { city: 'Komagane', prefecture: 'Nagano' },
    '389': { city: 'Matsumoto', prefecture: 'Nagano' },

    // Gifu (500-509)
    '500': { city: 'Gifu', prefecture: 'Gifu' },
    '501': { city: 'Gifu', prefecture: 'Gifu' },
    '502': { city: 'Gifu', prefecture: 'Gifu' },
    '503': { city: 'Hashima', prefecture: 'Gifu' },
    '504': { city: 'Ogaki', prefecture: 'Gifu' },
    '505': { city: 'Ogaki', prefecture: 'Gifu' },
    '506': { city: 'Yoro', prefecture: 'Gifu' },
    '507': { city: 'Kakamigahara', prefecture: 'Gifu' },
    '508': { city: 'Mizunami', prefecture: 'Gifu' },
    '509': { city: 'Gujō', prefecture: 'Gifu' },

    // Shizuoka (420-429)
    '420': { city: 'Shizuoka', prefecture: 'Shizuoka' },
    '421': { city: 'Shizuoka', prefecture: 'Shizuoka' },
    '422': { city: 'Shimizu', prefecture: 'Shizuoka' },
    '423': { city: 'Yui', prefecture: 'Shizuoka' },
    '424': { city: 'Numazu', prefecture: 'Shizuoka' },
    '425': { city: 'Mishima', prefecture: 'Shizuoka' },
    '426': { city: 'Atami', prefecture: 'Shizuoka' },
    '427': { city: 'Izu', prefecture: 'Shizuoka' },
    '428': { city: 'Kawazu', prefecture: 'Shizuoka' },
    '429': { city: 'Gotemba', prefecture: 'Shizuoka' },

    // Aichi (450-459)
    '450': { city: 'Nagoya', prefecture: 'Aichi' },
    '451': { city: 'Nagoya', prefecture: 'Aichi' },
    '452': { city: 'Nagoya', prefecture: 'Aichi' },
    '453': { city: 'Nagoya', prefecture: 'Aichi' },
    '454': { city: 'Nagoya', prefecture: 'Aichi' },
    '455': { city: 'Toyohashi', prefecture: 'Aichi' },
    '456': { city: 'Toyohashi', prefecture: 'Aichi' },
    '457': { city: 'Toyohashi', prefecture: 'Aichi' },
    '458': { city: 'Toyokawa', prefecture: 'Aichi' },
    '459': { city: 'Okazaki', prefecture: 'Aichi' },

    // Mie (510-519)
    '510': { city: 'Tsu', prefecture: 'Mie' },
    '511': { city: 'Tsu', prefecture: 'Mie' },
    '512': { city: 'Yokkaichi', prefecture: 'Mie' },
    '513': { city: 'Suzuka', prefecture: 'Mie' },
    '514': { city: 'Matsusaka', prefecture: 'Mie' },
    '515': { city: 'Ise', prefecture: 'Mie' },
    '516': { city: 'Watarai', prefecture: 'Mie' },
    '517': { city: 'Shima', prefecture: 'Mie' },
    '518': { city: 'Kumano', prefecture: 'Mie' },
    '519': { city: 'Owase', prefecture: 'Mie' },

    // Shiga (520-529)
    '520': { city: 'Ōtsu', prefecture: 'Shiga' },
    '521': { city: 'Ōtsu', prefecture: 'Shiga' },
    '522': { city: 'Kusatsu', prefecture: 'Shiga' },
    '523': { city: 'Ritto', prefecture: 'Shiga' },
    '524': { city: 'Yasu', prefecture: 'Shiga' },
    '525': { city: 'Moriyama', prefecture: 'Shiga' },
    '526': { city: 'Hikone', prefecture: 'Shiga' },
    '527': { city: 'Nagahama', prefecture: 'Shiga' },
    '528': { city: 'Maibara', prefecture: 'Shiga' },
    '529': { city: 'Takashima', prefecture: 'Shiga' },

    // Kyoto (600-629)
    '600': { city: 'Kyoto', prefecture: 'Kyoto' },
    '601': { city: 'Kyoto', prefecture: 'Kyoto' },
    '602': { city: 'Kyoto', prefecture: 'Kyoto' },
    '603': { city: 'Kyoto', prefecture: 'Kyoto' },
    '604': { city: 'Kyoto', prefecture: 'Kyoto' },
    '605': { city: 'Kyoto', prefecture: 'Kyoto' },
    '606': { city: 'Kyoto', prefecture: 'Kyoto' },
    '607': { city: 'Nantan', prefecture: 'Kyoto' },
    '608': { city: 'Nanzen', prefecture: 'Kyoto' },
    '609': { city: 'Fukuchiyama', prefecture: 'Kyoto' },
    '610': { city: 'Kameoka', prefecture: 'Kyoto' },
    '611': { city: 'Yawata', prefecture: 'Kyoto' },
    '612': { city: 'Uji', prefecture: 'Kyoto' },
    '613': { city: 'Joyo', prefecture: 'Kyoto' },
    '614': { city: 'Kasagi', prefecture: 'Kyoto' },
    '615': { city: 'Kizugawa', prefecture: 'Kyoto' },
    '616': { city: 'Ayabe', prefecture: 'Kyoto' },
    '617': { city: 'Kizugawa', prefecture: 'Kyoto' },
    '618': { city: 'Nanzo', prefecture: 'Kyoto' },
    '619': { city: 'Kizugawa', prefecture: 'Kyoto' },
    '620': { city: 'Miyazu', prefecture: 'Kyoto' },
    '621': { city: 'Yosano', prefecture: 'Kyoto' },
    '622': { city: 'Maizuru', prefecture: 'Kyoto' },
    '623': { city: 'Maizuru', prefecture: 'Kyoto' },
    '624': { city: 'Kyotango', prefecture: 'Kyoto' },
    '625': { city: 'Taiza', prefecture: 'Kyoto' },
    '626': { city: 'Hyogo', prefecture: 'Kyoto' },
    '627': { city: 'Nanba', prefecture: 'Kyoto' },
    '628': { city: 'Kasumi', prefecture: 'Kyoto' },
    '629': { city: 'Hyogo', prefecture: 'Kyoto' },

    // Osaka (530-589)
    '530': { city: 'Osaka', prefecture: 'Osaka' },
    '531': { city: 'Osaka', prefecture: 'Osaka' },
    '532': { city: 'Osaka', prefecture: 'Osaka' },
    '533': { city: 'Osaka', prefecture: 'Osaka' },
    '534': { city: 'Osaka', prefecture: 'Osaka' },
    '535': { city: 'Osaka', prefecture: 'Osaka' },
    '536': { city: 'Osaka', prefecture: 'Osaka' },
    '537': { city: 'Osaka', prefecture: 'Osaka' },
    '538': { city: 'Osaka', prefecture: 'Osaka' },
    '539': { city: 'Osaka', prefecture: 'Osaka' },
    '540': { city: 'Osaka', prefecture: 'Osaka' },
    '541': { city: 'Osaka', prefecture: 'Osaka' },
    '542': { city: 'Osaka', prefecture: 'Osaka' },
    '543': { city: 'Osaka', prefecture: 'Osaka' },
    '544': { city: 'Osaka', prefecture: 'Osaka' },
    '545': { city: 'Osaka', prefecture: 'Osaka' },
    '546': { city: 'Osaka', prefecture: 'Osaka' },
    '547': { city: 'Osaka', prefecture: 'Osaka' },
    '548': { city: 'Osaka', prefecture: 'Osaka' },
    '549': { city: 'Osaka', prefecture: 'Osaka' },
    '550': { city: 'Osaka', prefecture: 'Osaka' },
    '551': { city: 'Osaka', prefecture: 'Osaka' },
    '552': { city: 'Osaka', prefecture: 'Osaka' },
    '553': { city: 'Osaka', prefecture: 'Osaka' },
    '554': { city: 'Osaka', prefecture: 'Osaka' },
    '555': { city: 'Osaka', prefecture: 'Osaka' },
    '556': { city: 'Osaka', prefecture: 'Osaka' },
    '557': { city: 'Osaka', prefecture: 'Osaka' },
    '558': { city: 'Osaka', prefecture: 'Osaka' },
    '559': { city: 'Osaka', prefecture: 'Osaka' },
    '560': { city: 'Osaka', prefecture: 'Osaka' },
    '561': { city: 'Osaka', prefecture: 'Osaka' },
    '562': { city: 'Osaka', prefecture: 'Osaka' },
    '563': { city: 'Osaka', prefecture: 'Osaka' },
    '564': { city: 'Osaka', prefecture: 'Osaka' },
    '565': { city: 'Osaka', prefecture: 'Osaka' },
    '566': { city: 'Osaka', prefecture: 'Osaka' },
    '567': { city: 'Osaka', prefecture: 'Osaka' },
    '568': { city: 'Osaka', prefecture: 'Osaka' },
    '569': { city: 'Takarazuka', prefecture: 'Osaka' },
    '570': { city: 'Neyagawa', prefecture: 'Osaka' },
    '571': { city: 'Neyagawa', prefecture: 'Osaka' },
    '572': { city: 'Moriguchi', prefecture: 'Osaka' },
    '573': { city: 'Hirakata', prefecture: 'Osaka' },
    '574': { city: 'Katano', prefecture: 'Osaka' },
    '575': { city: 'Izumisano', prefecture: 'Osaka' },
    '576': { city: 'Izumi', prefecture: 'Osaka' },
    '577': { city: 'Kishiwada', prefecture: 'Osaka' },
    '578': { city: 'Kainan', prefecture: 'Osaka' },
    '579': { city: 'Yodogawa', prefecture: 'Osaka' },
    '580': { city: 'Shijonawate', prefecture: 'Osaka' },
    '581': { city: 'Higashiosaka', prefecture: 'Osaka' },
    '582': { city: 'Matsubara', prefecture: 'Osaka' },
    '583': { city: 'Daito', prefecture: 'Osaka' },
    '584': { city: 'Fujiidera', prefecture: 'Osaka' },
    '585': { city: 'Habikino', prefecture: 'Osaka' },
    '586': { city: 'Izumiotsu', prefecture: 'Osaka' },
    '587': { city: 'Sennan', prefecture: 'Osaka' },
    '588': { city: 'Hannan', prefecture: 'Osaka' },
    '589': { city: 'Tajiri', prefecture: 'Osaka' },

    // Hyogo (630-679)
    '630': { city: 'Kobe', prefecture: 'Hyogo' },
    '631': { city: 'Kobe', prefecture: 'Hyogo' },
    '632': { city: 'Kobe', prefecture: 'Hyogo' },
    '633': { city: 'Kobe', prefecture: 'Hyogo' },
    '634': { city: 'Kobe', prefecture: 'Hyogo' },
    '635': { city: 'Kobe', prefecture: 'Hyogo' },
    '636': { city: 'Kobe', prefecture: 'Hyogo' },
    '637': { city: 'Akashi', prefecture: 'Hyogo' },
    '638': { city: 'Akashi', prefecture: 'Hyogo' },
    '639': { city: 'Akashi', prefecture: 'Hyogo' },
    '640': { city: 'Himeji', prefecture: 'Hyogo' },
    '641': { city: 'Himeji', prefecture: 'Hyogo' },
    '642': { city: 'Himeji', prefecture: 'Hyogo' },
    '643': { city: 'Himeji', prefecture: 'Hyogo' },
    '644': { city: 'Ayabe', prefecture: 'Hyogo' },
    '645': { city: 'Takasago', prefecture: 'Hyogo' },
    '646': { city: 'Kawanishi', prefecture: 'Hyogo' },
    '647': { city: 'Kakogawa', prefecture: 'Hyogo' },
    '648': { city: 'Nishi', prefecture: 'Hyogo' },
    '649': { city: 'Harima', prefecture: 'Hyogo' },
    '650': { city: 'Amagasaki', prefecture: 'Hyogo' },
    '651': { city: 'Amagasaki', prefecture: 'Hyogo' },
    '652': { city: 'Nishinomiya', prefecture: 'Hyogo' },
    '653': { city: 'Nishinomiya', prefecture: 'Hyogo' },
    '654': { city: 'Ashiya', prefecture: 'Hyogo' },
    '655': { city: 'Takarazuka', prefecture: 'Hyogo' },
    '656': { city: 'Itami', prefecture: 'Hyogo' },
    '657': { city: 'Kawanishi', prefecture: 'Hyogo' },
    '658': { city: 'Sanda', prefecture: 'Hyogo' },
    '659': { city: 'Sannan', prefecture: 'Hyogo' },
    '660': { city: 'Ono', prefecture: 'Hyogo' },
    '661': { city: 'Miki', prefecture: 'Hyogo' },
    '662': { city: 'Minamiawaji', prefecture: 'Hyogo' },
    '663': { city: 'Sumoto', prefecture: 'Hyogo' },
    '664': { city: 'Minamiawaji', prefecture: 'Hyogo' },
    '665': { city: 'Awaji', prefecture: 'Hyogo' },
    '666': { city: 'Aizumi', prefecture: 'Hyogo' },
    '667': { city: 'Taiji', prefecture: 'Hyogo' },
    '668': { city: 'Toyooka', prefecture: 'Hyogo' },
    '669': { city: 'Kinosaki', prefecture: 'Hyogo' },
    '670': { city: 'Kasuga', prefecture: 'Hyogo' },
    '671': { city: 'Asago', prefecture: 'Hyogo' },
    '672': { city: 'Yumigahama', prefecture: 'Hyogo' },
    '673': { city: 'Wadayama', prefecture: 'Hyogo' },
    '674': { city: 'Hyogo', prefecture: 'Hyogo' },
    '675': { city: 'Shiso', prefecture: 'Hyogo' },
    '676': { city: 'Nanbu', prefecture: 'Hyogo' },
    '677': { city: 'Takasugi', prefecture: 'Hyogo' },
    '678': { city: 'Yashiro', prefecture: 'Hyogo' },
    '679': { city: 'Kasai', prefecture: 'Hyogo' },

    // Nara (630-639) — duplicate keys overwrite the Hyogo entries above (PHP last-wins).
    // In the PHP source these appear AFTER the Hyogo block, so 630-639 resolve to Nara.
    '630': { city: 'Nara', prefecture: 'Nara' },
    '631': { city: 'Nara', prefecture: 'Nara' },
    '632': { city: 'Yamato', prefecture: 'Nara' },
    '633': { city: 'Ikoma', prefecture: 'Nara' },
    '634': { city: 'Katsuragi', prefecture: 'Nara' },
    '635': { city: 'Gojo', prefecture: 'Nara' },
    '636': { city: 'Kashihara', prefecture: 'Nara' },
    '637': { city: 'Yamato', prefecture: 'Nara' },
    '638': { city: 'Gojyo', prefecture: 'Nara' },
    '639': { city: 'Nara', prefecture: 'Nara' },

    // Wakayama (640-649) — duplicate keys overwrite the Hyogo entries above (PHP last-wins).
    '640': { city: 'Wakayama', prefecture: 'Wakayama' },
    '641': { city: 'Wakayama', prefecture: 'Wakayama' },
    '642': { city: 'Wakayama', prefecture: 'Wakayama' },
    '643': { city: 'Arida', prefecture: 'Wakayama' },
    '644': { city: 'Shingu', prefecture: 'Wakayama' },
    '645': { city: 'Nachiykatsuura', prefecture: 'Wakayama' },
    '646': { city: 'Katsura', prefecture: 'Wakayama' },
    '647': { city: 'Tanabe', prefecture: 'Wakayama' },
    '648': { city: 'Minabe', prefecture: 'Wakayama' },
    '649': { city: 'Yuasa', prefecture: 'Wakayama' },

    // Tottori (680-689)
    '680': { city: 'Tottori', prefecture: 'Tottori' },
    '681': { city: 'Tottori', prefecture: 'Tottori' },
    '682': { city: 'Yonago', prefecture: 'Tottori' },
    '683': { city: 'Kurayoshi', prefecture: 'Tottori' },
    '684': { city: 'Wakasa', prefecture: 'Tottori' },
    '685': { city: 'Maisaka', prefecture: 'Tottori' },
    '686': { city: 'Hawai', prefecture: 'Tottori' },
    '687': { city: 'Daisen', prefecture: 'Tottori' },
    '688': { city: 'Koge', prefecture: 'Tottori' },
    '689': { city: 'Hoki', prefecture: 'Tottori' },

    // Shimane (690-699)
    '690': { city: 'Matsue', prefecture: 'Shimane' },
    '691': { city: 'Matsue', prefecture: 'Shimane' },
    '692': { city: 'Hamada', prefecture: 'Shimane' },
    '693': { city: 'Gotsu', prefecture: 'Shimane' },
    '694': { city: 'Kawamoto', prefecture: 'Shimane' },
    '695': { city: 'Unnan', prefecture: 'Shimane' },
    '696': { city: 'Izumo', prefecture: 'Shimane' },
    '697': { city: 'Izumo', prefecture: 'Shimane' },
    '698': { city: 'Sakaiminato', prefecture: 'Shimane' },
    '699': { city: 'Hidakamatsue', prefecture: 'Shimane' },

    // Okayama (700-709)
    '700': { city: 'Okayama', prefecture: 'Okayama' },
    '701': { city: 'Okayama', prefecture: 'Okayama' },
    '702': { city: 'Kurashiki', prefecture: 'Okayama' },
    '703': { city: 'Kurashiki', prefecture: 'Okayama' },
    '704': { city: 'Tsuyama', prefecture: 'Okayama' },
    '705': { city: 'Ibara', prefecture: 'Okayama' },
    '706': { city: 'Asahi', prefecture: 'Okayama' },
    '707': { city: 'Kasaoka', prefecture: 'Okayama' },
    '708': { city: 'Takahashi', prefecture: 'Okayama' },
    '709': { city: 'Asakuchi', prefecture: 'Okayama' },

    // Hiroshima (730-739)
    '730': { city: 'Hiroshima', prefecture: 'Hiroshima' },
    '731': { city: 'Hiroshima', prefecture: 'Hiroshima' },
    '732': { city: 'Hiroshima', prefecture: 'Hiroshima' },
    '733': { city: 'Hiroshima', prefecture: 'Hiroshima' },
    '734': { city: 'Hiroshima', prefecture: 'Hiroshima' },
    '735': { city: 'Kure', prefecture: 'Hiroshima' },
    '736': { city: 'Kure', prefecture: 'Hiroshima' },
    '737': { city: 'Takehara', prefecture: 'Hiroshima' },
    '738': { city: 'Kumagaya', prefecture: 'Hiroshima' },
    '739': { city: 'Onomichi', prefecture: 'Hiroshima' },

    // Yamaguchi (750-759)
    '750': { city: 'Yamaguchi', prefecture: 'Yamaguchi' },
    '751': { city: 'Yamaguchi', prefecture: 'Yamaguchi' },
    '752': { city: 'Shimonoseki', prefecture: 'Yamaguchi' },
    '753': { city: 'Shimonoseki', prefecture: 'Yamaguchi' },
    '754': { city: 'Shimonoseki', prefecture: 'Yamaguchi' },
    '755': { city: 'Iwakuni', prefecture: 'Yamaguchi' },
    '756': { city: 'Yanai', prefecture: 'Yamaguchi' },
    '757': { city: 'Kurashiki', prefecture: 'Yamaguchi' },
    '758': { city: 'Waki', prefecture: 'Yamaguchi' },
    '759': { city: 'Ishin', prefecture: 'Yamaguchi' },

    // Tokushima (770-779)
    '770': { city: 'Tokushima', prefecture: 'Tokushima' },
    '771': { city: 'Tokushima', prefecture: 'Tokushima' },
    '772': { city: 'Komatsushima', prefecture: 'Tokushima' },
    '773': { city: 'Wajima', prefecture: 'Tokushima' },
    '774': { city: 'Matsushige', prefecture: 'Tokushima' },
    '775': { city: 'Anan', prefecture: 'Tokushima' },
    '776': { city: 'Kamikatsu', prefecture: 'Tokushima' },
    '777': { city: 'Miyoshi', prefecture: 'Tokushima' },
    '778': { city: 'Anan', prefecture: 'Tokushima' },
    '779': { city: 'Higashi', prefecture: 'Tokushima' },

    // Kagawa (760-769)
    '760': { city: 'Takamatsu', prefecture: 'Kagawa' },
    '761': { city: 'Takamatsu', prefecture: 'Kagawa' },
    '762': { city: 'Marugame', prefecture: 'Kagawa' },
    '763': { city: 'Sakaide', prefecture: 'Kagawa' },
    '764': { city: 'Aji', prefecture: 'Kagawa' },
    '765': { city: 'Sanuki', prefecture: 'Kagawa' },
    '766': { city: 'Mitoyo', prefecture: 'Kagawa' },
    '767': { city: 'Nakatsu', prefecture: 'Kagawa' },
    '768': { city: 'Manotsuku', prefecture: 'Kagawa' },
    '769': { city: 'Utazu', prefecture: 'Kagawa' },

    // Ehime (790-799)
    '790': { city: 'Matsuyama', prefecture: 'Ehime' },
    '791': { city: 'Matsuyama', prefecture: 'Ehime' },
    '792': { city: 'Imabari', prefecture: 'Ehime' },
    '793': { city: 'Imabari', prefecture: 'Ehime' },
    '794': { city: 'Yawatahama', prefecture: 'Ehime' },
    '795': { city: 'Uvajima', prefecture: 'Ehime' },
    '796': { city: 'Ozu', prefecture: 'Ehime' },
    '797': { city: 'Iyo', prefecture: 'Ehime' },
    '798': { city: 'Kihoku', prefecture: 'Ehime' },
    '799': { city: 'Akehama', prefecture: 'Ehime' },

    // Kochi (780-789)
    '780': { city: 'Kochi', prefecture: 'Kochi' },
    '781': { city: 'Kochi', prefecture: 'Kochi' },
    '782': { city: 'Ino', prefecture: 'Kochi' },
    '783': { city: 'Tosa', prefecture: 'Kochi' },
    '784': { city: 'Kochi', prefecture: 'Kochi' },
    '785': { city: 'Nakamura', prefecture: 'Kochi' },
    '786': { city: 'Shimanto', prefecture: 'Kochi' },
    '787': { city: 'Susaki', prefecture: 'Kochi' },
    '788': { city: 'Oka', prefecture: 'Kochi' },
    '789': { city: 'Umaji', prefecture: 'Kochi' },

    // Fukuoka (810-819)
    '810': { city: 'Fukuoka', prefecture: 'Fukuoka' },
    '811': { city: 'Fukuoka', prefecture: 'Fukuoka' },
    '812': { city: 'Fukuoka', prefecture: 'Fukuoka' },
    '813': { city: 'Fukuoka', prefecture: 'Fukuoka' },
    '814': { city: 'Kurume', prefecture: 'Fukuoka' },
    '815': { city: 'Kurume', prefecture: 'Fukuoka' },
    '816': { city: 'Yaratsutsu', prefecture: 'Fukuoka' },
    '817': { city: 'Yame', prefecture: 'Fukuoka' },
    '818': { city: 'Yame', prefecture: 'Fukuoka' },
    '819': { city: 'Omuta', prefecture: 'Fukuoka' },

    // Saga (840-849)
    '840': { city: 'Saga', prefecture: 'Saga' },
    '841': { city: 'Saga', prefecture: 'Saga' },
    '842': { city: 'Taku', prefecture: 'Saga' },
    '843': { city: 'Karatsu', prefecture: 'Saga' },
    '844': { city: 'Imari', prefecture: 'Saga' },
    '845': { city: 'Arita', prefecture: 'Saga' },
    '846': { city: 'Ogi', prefecture: 'Saga' },
    '847': { city: 'Takeo', prefecture: 'Saga' },
    '848': { city: 'Ureshino', prefecture: 'Saga' },
    '849': { city: 'Yoshinogari', prefecture: 'Saga' },

    // Nagasaki (850-859)
    '850': { city: 'Nagasaki', prefecture: 'Nagasaki' },
    '851': { city: 'Nagasaki', prefecture: 'Nagasaki' },
    '852': { city: 'Nagasaki', prefecture: 'Nagasaki' },
    '853': { city: 'Sasebo', prefecture: 'Nagasaki' },
    '854': { city: 'Sasebo', prefecture: 'Nagasaki' },
    '855': { city: 'Sasebo', prefecture: 'Nagasaki' },
    '856': { city: 'Isahaya', prefecture: 'Nagasaki' },
    '857': { city: 'Shimabara', prefecture: 'Nagasaki' },
    '858': { city: 'Minamitakaki', prefecture: 'Nagasaki' },
    '859': { city: 'Goto', prefecture: 'Nagasaki' },

    // Kumamoto (860-869)
    '860': { city: 'Kumamoto', prefecture: 'Kumamoto' },
    '861': { city: 'Kumamoto', prefecture: 'Kumamoto' },
    '862': { city: 'Kumamoto', prefecture: 'Kumamoto' },
    '863': { city: 'Kumamoto', prefecture: 'Kumamoto' },
    '864': { city: 'Asoan', prefecture: 'Kumamoto' },
    '865': { city: 'Yatsushiro', prefecture: 'Kumamoto' },
    '866': { city: 'Hondo', prefecture: 'Kumamoto' },
    '867': { city: 'Hitoyoshi', prefecture: 'Kumamoto' },
    '868': { city: 'Arao', prefecture: 'Kumamoto' },
    '869': { city: 'Uki', prefecture: 'Kumamoto' },

    // Oita (870-879)
    '870': { city: 'Oita', prefecture: 'Oita' },
    '871': { city: 'Oita', prefecture: 'Oita' },
    '872': { city: 'Beppu', prefecture: 'Oita' },
    '873': { city: 'Bepu', prefecture: 'Oita' },
    '874': { city: 'Usuki', prefecture: 'Oita' },
    '875': { city: 'Saiki', prefecture: 'Oita' },
    '876': { city: 'Taketa', prefecture: 'Oita' },
    '877': { city: 'Bungo', prefecture: 'Oita' },
    '878': { city: 'Nakatsu', prefecture: 'Oita' },
    '879': { city: 'Kusu', prefecture: 'Oita' },

    // Miyazaki (880-889)
    '880': { city: 'Miyazaki', prefecture: 'Miyazaki' },
    '881': { city: 'Miyazaki', prefecture: 'Miyazaki' },
    '882': { city: 'Miyazaki', prefecture: 'Miyazaki' },
    '883': { city: 'Shinchi', prefecture: 'Miyazaki' },
    '884': { city: 'Iyo', prefecture: 'Miyazaki' },
    '885': { city: 'Nichinan', prefecture: 'Miyazaki' },
    '886': { city: 'Kobayashi', prefecture: 'Miyazaki' },
    '887': { city: 'Kitagocho', prefecture: 'Miyazaki' },
    '888': { city: 'Takachiho', prefecture: 'Miyazaki' },
    '889': { city: 'Obi', prefecture: 'Miyazaki' },

    // Kagoshima (890-899)
    '890': { city: 'Kagoshima', prefecture: 'Kagoshima' },
    '891': { city: 'Kagoshima', prefecture: 'Kagoshima' },
    '892': { city: 'Kagoshima', prefecture: 'Kagoshima' },
    '893': { city: 'Aira', prefecture: 'Kagoshima' },
    '894': { city: 'Sendai', prefecture: 'Kagoshima' },
    '895': { city: 'Miyakonojo', prefecture: 'Kagoshima' },
    '896': { city: 'Makurazaki', prefecture: 'Kagoshima' },
    '897': { city: 'Nishinomiya', prefecture: 'Kagoshima' },
    '898': { city: 'Kanoya', prefecture: 'Kagoshima' },
    '899': { city: 'Tarumizu', prefecture: 'Kagoshima' },

    // Okinawa (900-909)
    '900': { city: 'Naha', prefecture: 'Okinawa' },
    '901': { city: 'Naha', prefecture: 'Okinawa' },
    '902': { city: 'Ginowan', prefecture: 'Okinawa' },
    '903': { city: 'Urasoe', prefecture: 'Okinawa' },
    '904': { city: 'Yomitan', prefecture: 'Okinawa' },
    '905': { city: 'Kadena', prefecture: 'Okinawa' },
    '906': { city: 'Koza', prefecture: 'Okinawa' },
    '907': { city: 'Nankoku', prefecture: 'Okinawa' },
    '908': { city: 'Kunigami', prefecture: 'Okinawa' },
    '909': { city: 'Motobu', prefecture: 'Okinawa' },
};
